import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import React, { useEffect, useState, useRef } from "react";
import { Editor } from "react-draft-wysiwyg";
import { EditorState, convertToRaw, ContentState } from "draft-js";
import draftToHtml from "draftjs-to-html";
import htmlToDraft from "html-to-draftjs";

const EditText = (props: {
    content: string;
    setContent: (arg0: string) => void;
}) => {
    // ✅ Keep track of what we most recently pushed to the parent to prevent loops
    const lastPushedHtml = useRef(props.content);

    // ✅ Initialize state only once
    const [editorState, setEditorState] = useState(() => {
        if (props.content && props.content.trim() !== "") {
            const blocksFromHtml = htmlToDraft(props.content);
            if (blocksFromHtml) {
                const { contentBlocks, entityMap } = blocksFromHtml;
                const contentState = ContentState.createFromBlockArray(contentBlocks, entityMap);
                return EditorState.createWithContent(contentState);
            }
        }
        return EditorState.createEmpty();
    });

    const handleEditorStateChange = (newState: any) => {
        setEditorState(newState);
    };

    // ✅ Debounced Real-time sync to parent
    useEffect(() => {
        const timeoutId = setTimeout(() => {
            const currentHtml = draftToHtml(convertToRaw(editorState.getCurrentContent()));
            if (currentHtml !== props.content && currentHtml !== lastPushedHtml.current) {
                lastPushedHtml.current = currentHtml;
                props.setContent(currentHtml);
            }
        }, 300); // 300ms debounce
        return () => clearTimeout(timeoutId);
    }, [editorState, props]);

    // ✅ Support external content updates (e.g. if the parent changes the content from outside)
    useEffect(() => {
        if (props.content !== lastPushedHtml.current) {
            const blocksFromHtml = htmlToDraft(props.content || "");
            if (blocksFromHtml) {
                const { contentBlocks, entityMap } = blocksFromHtml;
                const contentState = ContentState.createFromBlockArray(contentBlocks, entityMap);
                setEditorState(EditorState.createWithContent(contentState));
                lastPushedHtml.current = props.content;
            }
        }
    }, [props.content]);

    return (
        <div className="dynamic_editor" style={{ overflow: "visible" }}>
            <Editor
                toolbar={{
                    options: [
                        "inline",
                        "fontSize",
                        "list",
                        "textAlign",
                        "link",
                        "colorPicker",
                        "image",
                        "remove",
                        "history",
                    ],
                    inline: {
                        options: ["bold", "italic", "underline"],
                    },
                    fontSize: {
                        options: [10, 12, 14, 16, 18, 24, 32],
                    },
                    list: {
                        options: ["unordered", "ordered"],
                    },
                    textAlign: {
                        options: ["left", "center", "right"],
                    },
                    link: {
                        options: ["link", "unlink"],
                        showOpenOptionOnHover: true,
                    },
                    colorPicker: {
                        colors: [
                            "#000000",
                            "#FF0000",
                            "#00FF00",
                            "#0000FF",
                            "#FB4898",
                        ],
                    },
                    image: {
                        uploadEnabled: true,
                        previewImage: true,
                        alignmentEnabled: true,
                        inputAccept: "image/*",
                        uploadCallback: async (file: File) => {
                            return new Promise((resolve) => {
                                const reader = new FileReader();
                                reader.onload = () => {
                                    resolve({
                                        data: { link: reader.result },
                                    });
                                };
                                reader.readAsDataURL(file);
                            });
                        },
                    },
                }}
                editorState={editorState}
                onEditorStateChange={handleEditorStateChange}
                editorStyle={{
                    height: "40vh",
                    border: "1px solid #E6E8F0",
                    padding: 10,
                }}
            />
        </div>
    );
};

export default EditText;